/* eslint-disable import/prefer-default-export */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Big from 'big.js';
import {
  cloneDeep, isEmpty, without, find
} from 'lodash';

import { pluck } from '../../../../util/utils';
import { applyDiscounts, orderTotals } from '../../../../util/orderUtils';
import { openSidebar, closeSidebar } from '../services/orderSidebarService';
import { orderCurrencySymbol } from '../../../../services/orderService';
import { DEFAULT_SUMMARY } from '../constants/orderSummaryConstants';

export const useOrderItems = ({
  getValues,
  setValue,
  useWatch,
  control
}) => {
  const dispatch = useDispatch();

  const { retailer_info = {} } = useSelector((reduxState) => reduxState.retailerUsersReducer);

  const [selectedItems, setSelectedItems] = useState([]);
  const [orderSummary, setOrderSummary] = useState(DEFAULT_SUMMARY);

  const { currency_code } = retailer_info;

  const selectedProducts = useWatch({ name: 'selectedProducts', control });
  const removedSelectedProducts = useWatch({ name: 'removedSelectedProducts', control });
  const shipping_value = useWatch({ name: 'shipping_value', control });
  const discount_type = useWatch({ name: 'discount_type', control });
  const discount_value = useWatch({ name: 'discount_value', control });
  const discount_reason = useWatch({ name: 'discount_reason', control });
  const currency = useWatch({ name: 'currency', control }) || currency_code;

  const validItems = useMemo(
    () => selectedProducts.filter((el) => el._destroy !== true),
    [selectedProducts]
  );

  const customTaxes = useMemo(
    () => [...new Set(pluck(validItems, 'custom_tax').map((p) => Number(p)))],
    [validItems]
  );

  const isAllSelected = useMemo(() => {
    if (isEmpty(selectedProducts)) return false;
    return selectedProducts.every((product) => product.selected === true);
  }, [selectedProducts]);

  const currency_symbol = useMemo(
    () => orderCurrencySymbol({ currency }),
    [currency]
  );

  useEffect(() => {
    const orderFormData = getValues();
    const newSummary = orderTotals(selectedProducts, orderFormData);
    setOrderSummary(newSummary);
  }, [selectedProducts, shipping_value, discount_type, discount_value, getValues]);

  useEffect(() => {
    setValue('orderSummary', orderSummary);
  }, [orderSummary, setValue]);

  const handleSetSelectedProducts = useCallback((items) => {
    const orderFormData = getValues();
    const newItems = applyDiscounts(items.filter((el) => el._destroy !== true), orderFormData);
    setValue('selectedProducts', newItems);
  }, [getValues, setValue]);

  const selectAllProducts = useCallback((e) => {
    const { checked } = e.target;

    const newSelectProducts = selectedProducts.map((product) => ({
      ...product,
      selected: checked
    }));

    const newSelectedItems = newSelectProducts.filter((product) => product.selected);
    setSelectedItems(newSelectedItems);

    handleSetSelectedProducts(newSelectProducts);
  }, [handleSetSelectedProducts, selectedProducts]);

  const globalChangePriceToWholesalePrice = useCallback((applyWholesale) => {
    const newSelectedProducts = selectedProducts.map((product) => {
      if (!product.selected) return product;

      const hasWholesale = Number(product.wholesale_price) > 0;

      const wholesaleOrRegularPrice = hasWholesale ? product.wholesale_price : product.unit_price;
      const unitPrice = applyWholesale ? wholesaleOrRegularPrice : product.original_price;

      return {
        ...product,
        unit_price: unitPrice,
        sold_wholesale: applyWholesale && hasWholesale,
        total: Number(
          Big(product.quantity)
            .times(Number(unitPrice))
            .round(2)
        ),
        total_discount: 0,
        selected: false
      };
    });

    handleSetSelectedProducts(newSelectedProducts);

    const newSelectedItems = newSelectedProducts.filter((product) => product.selected);
    setSelectedItems(newSelectedItems);
  }, [selectedProducts, handleSetSelectedProducts]);

  const handlePriceChange = useCallback((value) => {
    const applyWholesale = value === 'wholesalePrice';
    globalChangePriceToWholesalePrice(applyWholesale);
  }, [globalChangePriceToWholesalePrice]);

  const selectOrderItem = useCallback((e, lineItem) => {
    const newSelectedProducts = selectedProducts.map((product) => {
      if (product.lineIdentifier !== lineItem.lineIdentifier) return product;
      return {
        ...product,
        selected: e.target.checked
      };
    });

    const newSelectedItems = newSelectedProducts.filter((product) => product.selected);
    setSelectedItems(newSelectedItems);

    handleSetSelectedProducts(newSelectedProducts);
  }, [handleSetSelectedProducts, selectedProducts]);

  const persistValues = useCallback((product, event) => {
    const { name, value } = event.target;

    let updatedProduct = { ...product };

    if (name.includes('quantity')) {
      updatedProduct.quantity = value;
      updatedProduct = setTotalForLineItem(updatedProduct);
    } else if (name.includes('unit_price')) {
      updatedProduct.sold_wholesale = Number(updatedProduct.wholesale_price) === Number(value);
      updatedProduct.unit_price = value;
      updatedProduct = setTotalForLineItem(updatedProduct);
    } else if (name.includes('custom_tax')) {
      updatedProduct.custom_tax = value;
    }

    return updatedProduct;
  }, []);

  const handleInputEdit = useCallback((event, lineItem) => {
    const newSelectedProducts = selectedProducts.map((product) => (product.lineIdentifier === lineItem.lineIdentifier
      ? persistValues(product, event)
      : product));

    handleSetSelectedProducts(newSelectedProducts);
  }, [selectedProducts, handleSetSelectedProducts, persistValues]);

  const setTotalForLineItem = (lineItem) => {
    const total = Number(Big(Number(lineItem.quantity)).times(Number(lineItem.unit_price)).round(2));

    return {
      ...lineItem,
      total,
      total_discount: 0
    };
  };

  const setEditTax = useCallback((lineItem, action) => {
    const clonedSelectedProducts = cloneDeep(selectedProducts);
    const lineItemIn = getLineItem(clonedSelectedProducts, lineItem);

    if (!lineItemIn) return;

    lineItemIn.editableCustomTax = action;
    handleSetSelectedProducts(clonedSelectedProducts);
  }, [selectedProducts, handleSetSelectedProducts]);

  const getLineItem = (array = [], lineItem) => find(array, { lineIdentifier: lineItem.lineIdentifier });

  const openProductDiscount = useCallback((product) => {
    const orderFormData = getValues();
    let newLineItem = { ...product };

    if (newLineItem.forOrder) {
      newLineItem = {
        ...newLineItem,
        discount_type: orderFormData.discount_type,
        discount_value: orderFormData.discount_value,
        discount_reason: orderFormData.discount_reason
      };
    }

    openSidebar(dispatch, {
      componentKey: 'ManageProductDiscountModal',
      className: "tw-w-[60vw]",
      props: {
        lineItem: newLineItem,
        selectedProducts,
        handleSetSelectedProducts,
        updateOrder: (data) => {
          Object.entries(data).forEach(([key, value]) => setValue(key, value));
        }
      }
    });
  }, [dispatch, selectedProducts, handleSetSelectedProducts, getValues, setValue]);

  const removeProduct = useCallback((product) => {
    if (product.id) {
      setValue('removedSelectedProducts', [...removedSelectedProducts, product]);
    }

    handleSetSelectedProducts(without(selectedProducts, product));
  }, [removedSelectedProducts, selectedProducts, setValue, handleSetSelectedProducts]);

  const addProducts = useCallback((newProducts) => {
    handleSetSelectedProducts([...selectedProducts, ...newProducts]);
    closeSidebar(dispatch, 'SearchProductsModal');
  }, [handleSetSelectedProducts, selectedProducts, dispatch]);

  const addNewLineItem = useCallback(() => {
    openSidebar(dispatch, {
      componentKey: 'SearchProductsModal',
      className: "tw-w-[60vw]",
      props: {
        selectedProducts,
        addProducts
      }
    });
  }, [addProducts, dispatch, selectedProducts]);

  const handleOrderDiscount = useCallback(() => {
    const orderFormData = getValues();
    openProductDiscount({ ...orderFormData, forOrder: true });
  }, [getValues, openProductDiscount]);

  return {
    selectedAllProducts: isAllSelected,
    selectedItems,
    orderSummary,
    selectedProducts,
    validItems,
    customTaxes,
    currency,
    currency_symbol,
    discount_type,
    discount_value,
    discount_reason,
    addNewLineItem,
    selectAllProducts,
    handlePriceChange,
    openProductDiscount,
    selectOrderItem,
    handleInputEdit,
    setEditTax,
    removeProduct,
    handleOrderDiscount
  };
};
